/**
 * ONE global audio player for the whole platform.
 *
 * Every discrete "track" (guided meditation, sound bath, sleep audio, EFT or
 * bilateral recording, learn-page audio) plays through a single shared
 * <audio> element owned by this module. Because there is only one element,
 * two tracks can never overlap: asking to play a new track stops the current
 * one first.
 *
 * It also registers with the audio bus, so screens that make sound another way
 * (Web Audio soundscapes, spoken sequences) stop this player when they start,
 * and are stopped by it.
 *
 * Nothing here ever starts on its own — playback always begins from a tap.
 */
import { registerAudioOwner, claimAudio, type AudioOwner } from "@/lib/audio-bus";
import { armPlaybackSession } from "@/lib/audio-context";

export type AudioTrackKind = "spoken" | "ambient";

export type AudioTrack = {
  /** Stable id, used for play-state checks and remembered position. */
  id: string;
  title: string;
  subtitle?: string;
  src: string;
  kind?: AudioTrackKind;
  /** Loop ambient/soundscape recordings. Spoken content never loops. */
  loop?: boolean;
  /** Optional known length in seconds, shown before metadata loads. */
  durationHint?: number;
  /** Where to send someone who taps the mini-player title. */
  href?: string;
};

export type AudioStatus = "idle" | "loading" | "playing" | "paused" | "error";

export type AudioPlayerState = {
  track: AudioTrack | null;
  status: AudioStatus;
  currentTime: number;
  duration: number;
  rate: number;
  volume: number;
  muted: boolean;
  error: string | null;
  /** Remaining sleep-timer seconds, or null when no timer is set. */
  sleepRemaining: number | null;
  sleepMinutes: number | null;
};

const POSITIONS_KEY = "rt:audio-positions:v1";
const PREFS_KEY = "rt:audio-prefs:v1";
/** Only remember a position for content long enough for it to matter. */
const REMEMBER_MIN_DURATION = 300;

let state: AudioPlayerState = {
  track: null,
  status: "idle",
  currentTime: 0,
  duration: 0,
  rate: 1,
  volume: 1,
  muted: false,
  error: null,
  sleepRemaining: null,
  sleepMinutes: null,
};

const listeners = new Set<() => void>();
let el: HTMLAudioElement | null = null;
let sleepInterval: number | null = null;
let owner: AudioOwner | null = null;

function emit() {
  for (const l of listeners) l();
}

function set(patch: Partial<AudioPlayerState>) {
  state = { ...state, ...patch };
  emit();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getState(): AudioPlayerState {
  return state;
}

/* ---------------------------------------------------------------- storage */

function readMap(key: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function rememberPosition(id: string, seconds: number, duration: number) {
  if (duration < REMEMBER_MIN_DURATION) return;
  try {
    const map = readMap(POSITIONS_KEY);
    if (seconds < 20 || seconds > duration - 20) delete map[id];
    else map[id] = Math.floor(seconds);
    localStorage.setItem(POSITIONS_KEY, JSON.stringify(map));
  } catch {
    /* storage can be unavailable in private mode */
  }
}

export function getRememberedPosition(id: string): number {
  return readMap(POSITIONS_KEY)[id] ?? 0;
}

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return;
    const p = JSON.parse(raw) as Partial<Pick<AudioPlayerState, "rate" | "volume" | "muted">>;
    state = {
      ...state,
      rate: typeof p.rate === "number" ? p.rate : state.rate,
      volume: typeof p.volume === "number" ? p.volume : state.volume,
      muted: typeof p.muted === "boolean" ? p.muted : state.muted,
    };
  } catch {
    /* ignore */
  }
}

function savePrefs() {
  try {
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({ rate: state.rate, volume: state.volume, muted: state.muted }),
    );
  } catch {
    /* ignore */
  }
}

/* ----------------------------------------------------------------- engine */

function ensureElement(): HTMLAudioElement | null {
  if (typeof window === "undefined" || typeof Audio === "undefined") return null;
  if (el) return el;

  loadPrefs();
  const audio = new Audio();
  audio.preload = "metadata";
  audio.volume = state.volume;
  audio.muted = state.muted;
  audio.dataset.globalPlayer = "true";

  audio.addEventListener("loadedmetadata", () => {
    set({ duration: Number.isFinite(audio.duration) ? audio.duration : state.track?.durationHint ?? 0 });
    updateMediaSession();
  });
  audio.addEventListener("waiting", () => {
    if (state.status === "playing") set({ status: "loading" });
  });
  audio.addEventListener("playing", () => set({ status: "playing", error: null }));
  audio.addEventListener("play", () => set({ status: "playing", error: null }));
  audio.addEventListener("pause", () => {
    if (state.status !== "idle" && state.status !== "error") set({ status: "paused" });
    if (state.track) rememberPosition(state.track.id, audio.currentTime, audio.duration || 0);
  });
  audio.addEventListener("timeupdate", () => {
    set({ currentTime: audio.currentTime });
  });
  audio.addEventListener("ended", () => {
    if (state.track) rememberPosition(state.track.id, 0, audio.duration || 0);
    set({ status: "paused", currentTime: 0 });
    try {
      audio.currentTime = 0;
    } catch {
      /* ignore */
    }
  });
  audio.addEventListener("error", () => {
    set({
      status: "error",
      error: "This audio could not be loaded. Please check your connection and try again.",
    });
  });

  el = audio;

  owner = {
    stop: () => {
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
    },
    ownsElement: (candidate) => candidate === audio,
    persistent: true,
  };
  registerAudioOwner(owner);

  return audio;
}

function updateMediaSession() {
  if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
  const ms = navigator.mediaSession;
  const track = state.track;
  if (!track) return;
  try {
    ms.metadata = new MediaMetadata({
      title: track.title,
      artist: track.subtitle || "Recovery Trauma",
      album: "Recovery Trauma",
    });
    ms.setActionHandler("play", () => void resume());
    ms.setActionHandler("pause", () => pause());
    ms.setActionHandler("stop", () => stop());
    ms.setActionHandler("seekbackward", () => skip(-15));
    ms.setActionHandler("seekforward", () => skip(15));
  } catch {
    /* media session is best-effort */
  }
}

/* ------------------------------------------------------------------- API  */

/** Start a track. Anything else making sound stops first. */
export function playTrack(track: AudioTrack): void {
  const audio = ensureElement();
  if (!audio) return;

  // Take the single audio slot before touching the element.
  claimAudio(owner);

  const isSame = state.track?.id === track.id && audio.src === track.src;
  if (!isSame) {
    if (state.track) rememberPosition(state.track.id, audio.currentTime, audio.duration || 0);
    audio.src = track.src;
    audio.loop = track.loop ?? false;
    audio.load();
    const resumeAt = getRememberedPosition(track.id);
    if (resumeAt > 0) {
      const seekWhenReady = () => {
        try {
          audio.currentTime = resumeAt;
        } catch {
          /* ignore */
        }
        audio.removeEventListener("loadedmetadata", seekWhenReady);
      };
      audio.addEventListener("loadedmetadata", seekWhenReady);
    }
    set({
      track,
      status: "loading",
      currentTime: resumeAt,
      duration: track.durationHint ?? 0,
      error: null,
    });
  } else {
    set({ track, status: "loading", error: null });
  }

  audio.playbackRate = track.kind === "ambient" ? 1 : state.rate;
  // A saved "muted" or near-zero volume made Play look like it worked with no
  // sound. Pressing Play always means "I want to hear this".
  if (state.muted || state.volume < 0.05) {
    set({ muted: false, volume: state.volume < 0.05 ? 1 : state.volume });
    savePrefs();
  }
  armPlaybackSession();
  audio.volume = state.volume;
  audio.muted = state.muted;

  void audio.play().catch((err: unknown) => {
    const name = (err as { name?: string })?.name;
    set({
      status: "error",
      error:
        name === "NotAllowedError"
          ? "Your device blocked playback. Tap play again, and check your phone isn't on silent."
          : "This audio could not start. Please tap play again.",
    });
  });
  updateMediaSession();
}

export function pause(): void {
  try {
    el?.pause();
  } catch {
    /* ignore */
  }
}

export function resume(): void {
  const audio = el;
  if (!audio || !state.track) return;
  claimAudio(owner);
  set({ status: "loading", error: null });
  void audio.play().catch(() => {
    set({ status: "error", error: "This audio could not start. Please tap play again." });
  });
}

export function toggle(): void {
  if (state.status === "playing" || state.status === "loading") pause();
  else resume();
}

/** Close the player entirely and forget the loaded track. */
export function stop(): void {
  const audio = el;
  if (audio) {
    if (state.track) rememberPosition(state.track.id, audio.currentTime, audio.duration || 0);
    try {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    } catch {
      /* ignore */
    }
  }
  clearSleepTimer();
  set({ track: null, status: "idle", currentTime: 0, duration: 0, error: null });
}

export function seek(seconds: number): void {
  const audio = el;
  if (!audio) return;
  const max = Number.isFinite(audio.duration) ? audio.duration : state.duration;
  const next = Math.max(0, Math.min(max || 0, seconds));
  try {
    audio.currentTime = next;
  } catch {
    /* ignore */
  }
  set({ currentTime: next });
}

export function skip(delta: number): void {
  seek((el?.currentTime ?? state.currentTime) + delta);
}

export function setRate(rate: number): void {
  set({ rate });
  if (el && state.track?.kind !== "ambient") el.playbackRate = rate;
  savePrefs();
}

export function setVolume(volume: number): void {
  const v = Math.max(0, Math.min(1, volume));
  set({ volume: v, muted: v === 0 ? state.muted : false });
  if (el) {
    el.volume = v;
    if (v > 0) el.muted = state.muted;
  }
  savePrefs();
}

export function toggleMute(): void {
  const muted = !state.muted;
  set({ muted });
  if (el) el.muted = muted;
  savePrefs();
}

export function isTrackActive(id: string): boolean {
  return state.track?.id === id;
}

export function isTrackPlaying(id: string): boolean {
  return state.track?.id === id && (state.status === "playing" || state.status === "loading");
}

/* ------------------------------------------------------------ sleep timer */

function clearSleepTimer() {
  if (sleepInterval !== null) {
    window.clearInterval(sleepInterval);
    sleepInterval = null;
  }
  state = { ...state, sleepRemaining: null, sleepMinutes: null };
}

export function setSleepTimer(minutes: number | null): void {
  clearSleepTimer();
  if (minutes === null) {
    emit();
    return;
  }
  set({ sleepRemaining: minutes * 60, sleepMinutes: minutes });
  sleepInterval = window.setInterval(() => {
    const remaining = (state.sleepRemaining ?? 0) - 1;
    if (remaining <= 0) {
      clearSleepTimer();
      pause();
      emit();
      return;
    }
    set({ sleepRemaining: remaining });
  }, 1000);
}

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}
